import { parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import {
	json,
	type ActionFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import {
	Form,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useFetchers,
	useLoaderData,
	useMatches,
	useSubmit,
} from '@remix-run/react'
import { withSentry } from '@sentry/remix'
import { useRef } from 'react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuTrigger,
} from './components/ui/dropdown-menu.tsx'
import { Icon, href as iconsHref } from './components/ui/icon.tsx'
import { MoniToaster } from './components/ui/sonner.tsx'
import { BudgetSelect } from './routes/api+/budgets.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints, useHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import {
	cn,
	combineHeaders,
	getDomainUrl,
	getUserImgSrc,
} from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { getTheme, setTheme, type Theme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser, useUser } from './utils/user.ts'

export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		// Preload CSS as a resource to avoid render blocking
		{ rel: 'mask-icon', href: '/favicons/mask-icon.svg' },
		{
			rel: 'alternate icon',
			type: 'image/png',
			href: '/favicons/favicon-32x32.png',
		},
		{ rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		//These should match the css preloads above to avoid css as render blocking resource
		{ rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
		{
			rel: 'stylesheet',
			href: 'https://fonts.googleapis.com/css2?family=DM+Sans',
		},
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [
		{ title: data ? 'Moni' : 'Error | Moni' },
		{
			name: 'description',
			content: `Lleva el control de tus finanzas en pareja fácilmente`,
		},
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { id: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = honeypot.getInputProps()

	return json(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

const ThemeFormSchema = z.object({
	intent: z.literal('theme'),
	theme: z.enum(['system', 'light', 'dark']),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: ThemeFormSchema,
	})

	invariantResponse(
		submission.status === 'success',
		'Invalid form info received',
	)

	const { theme } = submission.value

	const responseInit = {
		headers: { 'set-cookie': setTheme(theme) },
	}
	return json({ result: submission.reply() }, responseInit)
}

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
	allowIndexing = true,
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string>
	allowIndexing?: boolean
}) {
	return (
		<html
			lang="es"
			className={`${theme} h-full overflow-x-hidden scroll-smooth`}
		>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

const menuItems = [
	{ label: 'Inicio', href: '/dashboard' },
	{ label: 'Presupuestos', href: '/budgets' },
	{ label: 'Gastos', href: '/expenses' },
	// { label: 'Configuración', href: '/settings/budget' },
	{ label: 'Reportes', href: '/reports' },
]

const menuWebItems = [
	{ label: 'Funcionalidades', href: '/#como-funciona' },
	{ label: 'Testimonios', href: '/#testimonios' },
	{ label: 'FAQ', href: '/#faq' },
]

const menuFooter = [
	{ label: 'Inicio', href: '/#inicio' },
	{ label: 'Cómo Funciona', href: '/#como-funciona' },
	{ label: 'Beneficios', href: '/#beneficios' },
	{ label: 'Planes y Precios', href: '/#planes-y-precios' },
	{ label: 'FAQs', href: '/#faqs' },
	{ label: 'Contacto', href: '/#contacto' },
]

const LandingWebLink = (item: { label: string; href: string }) => {
	return (
		<Link
			key={item.href}
			to={item.href}
			preventScrollReset
			className={cn(
				'text-xl inline-flex items-center rounded px-2 py-1 font-semibold',
				'bg-transparent hover:bg-tertiary',
			)}
		>
			{item.label}
		</Link>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()
	const user = useOptionalUser()
	const theme = useTheme()
	const matches = useMatches()
	const allowIndexing = data.ENV.ALLOW_INDEXING !== 'false'
	const inMarketing = matches.some(m => m.id.includes('_marketing+'))
	const inAuth = matches.some(m => m.id.includes('_auth+'))
	const inApp = !inMarketing && !inAuth
	useToast(data.toast)

	return (
		<Document
			nonce={nonce}
			theme={theme}
			allowIndexing={allowIndexing}
			env={data.ENV}
		>
			<div className="flex h-screen flex-col justify-between">
				<div
					className={cn({
						'bg-background-300': matches.some(m =>
							m.id.includes('_marketing+'),
						),
					})}
				>
					<header className="container py-6">
						<nav className="mb-3 flex flex-wrap items-center justify-between gap-4 sm:flex-nowrap md:gap-8">
							<Logo redirectTo={!inApp ? '/' : '/dashboard'} />
							{user && inApp ? (
								<div>
									<BudgetSelect />
								</div>
							) : null}
							<div className="flex items-center gap-10">
								{user && inApp ? (
									<UserDropdown />
								) : (
									<div className="inline-flex items-center">
										<div className="hidden px-4 md:block">
											{menuWebItems.map(item => (
												<LandingWebLink key={item.href} {...item} />
											))}
										</div>
										{user ? (
											<Button asChild variant="default" size="lg">
												<Link to="/dashboard">Ir al app</Link>
											</Button>
										) : (
											<Button asChild variant="default" size="lg">
												<Link to={encodeURI(`/login?redirectTo=/dashboard`)}>
													Ingresar
												</Link>
											</Button>
										)}
									</div>
								)}
							</div>
						</nav>
						{user && inApp ? (
							<div className="flex flex-col border-t border-primary/15 pt-2 md:flex-row md:space-x-1">
								{menuItems.map(item => (
									<Link
										key={item.href}
										to={item.href}
										className={cn(
											'text-sm inline-flex items-center rounded px-2 py-1 font-medium',
											matches.some(m => m.pathname.startsWith(item.href))
												? 'bg-tertiary'
												: 'bg-transparent hover:bg-tertiary',
										)}
									>
										{item.label}
									</Link>
								))}
							</div>
						) : null}
					</header>
				</div>

				<div className="flex-1">
					<Outlet />
				</div>
				{inMarketing ? (
					<footer className="bg-primary">
						<div className="container items-start justify-between pt-10 md:flex ">
							<div className="w-40 shrink-0 md:w-52">
								<Link to="/" className="group grid leading-snug">
									<img
										src={`/img/moni-dark.svg`}
										alt="Moni logo con una sonrisa"
										className="w-48"
									/>
								</Link>
							</div>
							<div className="grid w-full  grid-cols-2 gap-4 px-4 py-5 text-white  md:grid-cols-3">
								{menuFooter.map((item, index) => (
									<Link
										key={index}
										to={item.href}
										className="text-body-sm md:text-body-md"
									>
										<span className="mr-2 inline-block h-3 w-3 rounded-full bg-tertiary"></span>
										{item.label}
									</Link>
								))}
							</div>
							<div className="w-52 shrink-0">
								<h5 className="mb-4 text-h5 text-white">Síguenos en:</h5>
								<div className="flex gap-3">
									<Link to={'#'}>
										<img src="/img/marketing/fb_icon_light.svg" alt="fb" />
									</Link>
									<Link to={'#'}>
										<img src="/img/marketing/in_icon_light.svg" alt="ln" />
									</Link>
									<Link to={'#'}>
										<img src="/img/marketing/ig_icon_light.svg" alt="ig" />
									</Link>
								</div>
							</div>
						</div>
						<div className="container py-5 text-center text-body-sm text-white">
							Copyright © MONI 2024 - Todos los derechos reservados{' '}
							{/* <ThemeSwitch userPreference={data.requestInfo.userPrefs.theme} /> */}
						</div>
					</footer>
				) : null}
			</div>
			<MoniToaster closeButton position="top-right" theme={theme} />
			<EpicProgress />
		</Document>
	)
}

function Logo({ redirectTo = '/' }: { redirectTo?: string }) {
	const theme = useTheme()
	return (
		<Link to={redirectTo} className="group grid leading-snug">
			<img
				src={`/img/moni-${theme}.svg`}
				alt="Moni logo con una sonrisa"
				className="w-32"
			/>
		</Link>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default withSentry(AppWithProviders)

function UserDropdown() {
	const user = useUser()
	const submit = useSubmit()
	const formRef = useRef<HTMLFormElement>(null)
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					asChild
					variant="secondary"
					size="icon"
					className="rounded-full"
				>
					<Link
						to={`/users/${user.username}`}
						// this is for progressive enhancement
						onClick={e => e.preventDefault()}
						className="flex items-center gap-2"
					>
						<img
							className="h-9 w-9 rounded-full object-cover"
							alt={user.name ?? user.username}
							src={getUserImgSrc(user.image?.id)}
						/>
					</Link>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="end">
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/budgets`}>
							<Icon className="text-body-md" name="banknotes">
								Presupuestos
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/users/${user.username}`}>
							<Icon className="text-body-md" name="avatar">
								Perfil
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem
						asChild
						// this prevents the menu from closing before the form submission is completed
						onSelect={event => {
							event.preventDefault()
							submit(formRef.current)
						}}
					>
						<Form action="/logout" navigate={true} method="POST" ref={formRef}>
							<Icon className="text-body-md" name="exit">
								<button type="submit">Cerrar Sesión</button>
							</Icon>
						</Form>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme() {
	const hints = useHints()
	// const requestInfo = useRequestInfo()
	const optimisticMode = useOptimisticThemeMode()
	if (optimisticMode) {
		return optimisticMode === 'system' ? hints.theme : optimisticMode
	}
	// return requestInfo.userPrefs.theme ?? hints.theme
	return 'light'
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
export function useOptimisticThemeMode() {
	const fetchers = useFetchers()
	const themeFetcher = fetchers.find(f => f.formAction === '/')

	if (themeFetcher && themeFetcher.formData) {
		const submission = parseWithZod(themeFetcher.formData, {
			schema: ThemeFormSchema,
		})

		if (submission.status === 'success') {
			return submission.value.theme
		}
	}
}

// function ThemeSwitch({ userPreference }: { userPreference?: Theme | null }) {
// 	const fetcher = useFetcher<typeof action>()

// 	const [form] = useForm({
// 		id: 'theme-switch',
// 		lastResult: fetcher.data?.result,
// 	})

// 	// const optimisticMode = useOptimisticThemeMode()
// 	// const mode = optimisticMode ?? userPreference ?? 'system'
// 	const mode = 'light'
// 	const nextMode = 'light'
// 	// const nextMode =
// 	// 	mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system'
// 	const modeLabel = {
// 		light: (
// 			<Icon name="sun">
// 				<span className="sr-only">Light</span>
// 			</Icon>
// 		),
// 		dark: (
// 			<Icon name="moon">
// 				<span className="sr-only">Dark</span>
// 			</Icon>
// 		),
// 		system: (
// 			<Icon name="laptop">
// 				<span className="sr-only">System</span>
// 			</Icon>
// 		),
// 	}

// 	return (
// 		<fetcher.Form method="POST" {...getFormProps(form)}>
// 			<input type="hidden" name="intent" value="theme" />
// 			<input type="hidden" name="theme" value={nextMode} />
// 			<div className="flex gap-2">
// 				<button
// 					type="submit"
// 					className="flex h-8 w-8 cursor-pointer items-center justify-center"
// 				>
// 					{modeLabel[mode]}
// 				</button>
// 			</div>
// 		</fetcher.Form>
// 	)
// }

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce()

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it's possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you'll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>
	)
}
